// ============================================================
// SPEECH BUBBLE
// Displays toki pona glyphs above player heads with audio playback.
// Glyphs are white by default; the currently-spoken word is colored.
// Bubble disappears when all words finish playing.
// ============================================================

import Phaser from 'phaser';
import { TILE_SIZE } from '@blockworld/shared';
import { GLYPH_SIZE, getWord, getGlyphColor, getWordSpeeds } from '../toki/TokiPonaData';
import type { SpeedKey } from '../toki/TokiPonaData';
import type { TokiPonaAudio, AudioChannel, AudioProfile } from '../toki/TokiPonaAudio';

// Layout constants
const MAX_COLS = 7;
const BLOCKS_ABOVE_HEAD = 2; // Gap in tiles between player head top and bubble bottom

export interface SpeechBubbleConfig {
    scene: Phaser.Scene;
    words: number[]; // Array of glyph indices
    playerScreenX: number; // Screen-space X of player's top-left corner
    playerScreenY: number; // Screen-space Y of player's head top
    audio: TokiPonaAudio | null; // Audio loader (each bubble gets its own channel)
    profile?: AudioProfile; // Voice profile (defaults to pona/Friend)
}

export class SpeechBubble {
    private scene: Phaser.Scene;
    private container: Phaser.GameObjects.Container;
    private glyphs: Phaser.GameObjects.Image[] = [];
    private words: number[];
    private destroyed = false;

    private cols: number;
    private rows: number;
    private bubbleWidth: number;
    private bubbleHeight: number;

    // Audio playback state — each bubble gets its own channel for concurrent playback
    private audio: TokiPonaAudio | null;
    private profile: AudioProfile | undefined;
    private channel: AudioChannel | null;
    private currentWordIndex = -1;
    private playbackActive = false;

    constructor(config: SpeechBubbleConfig) {
        this.scene = config.scene;
        this.words = config.words;
        this.audio = config.audio;
        this.profile = config.profile;
        this.channel = config.audio?.createChannel(config.profile) ?? null;

        // Calculate layout (prefer single row, expand to 2 rows if needed)
        if (this.words.length <= MAX_COLS) {
            this.cols = this.words.length;
            this.rows = 1;
        } else {
            this.cols = MAX_COLS;
            this.rows = 2;
        }

        this.bubbleWidth = this.cols * GLYPH_SIZE;
        this.bubbleHeight = this.rows * GLYPH_SIZE;

        // Compute initial screen position
        const { x: bubbleX, y: bubbleY } = this.computeScreenPosition(
            config.playerScreenX,
            config.playerScreenY
        );

        // Container — just holds the glyph images
        this.container = this.scene.add.container(bubbleX, bubbleY);
        this.container.setDepth(150);

        // Glyph sprites — all start white (0xffffff)
        for (let i = 0; i < this.words.length; i++) {
            const col = i % this.cols;
            const row = Math.floor(i / this.cols);

            const glyph = this.scene.add.image(col * GLYPH_SIZE, row * GLYPH_SIZE, 'toki_glyphs', this.words[i]);
            glyph.setOrigin(0, 0);
            glyph.setTint(0xffffff);
            this.glyphs.push(glyph);
            this.container.add(glyph);
        }

        // Start audio-driven playback
        this.startPlayback();
    }

    private async startPlayback(): Promise<void> {
        if (!this.channel && this.audio) {
            // Audio system exists but channel creation failed (decode may be in progress).
            // Retry up to 3 times with short delays to let async decode complete.
            for (let attempt = 0; attempt < 3 && !this.channel && !this.destroyed; attempt++) {
                await new Promise(r => setTimeout(r, 150));
                this.channel = this.audio.createChannel(this.profile) ?? null;
            }
        }

        console.log(`[SpeechBubble] startPlayback: channel=${!!this.channel} words=${this.words.length} destroyed=${this.destroyed}`);

        if (!this.channel) {
            // No audio available — fall back to timed display with a visual sweep
            this.fallbackTimedDisplay();
            return;
        }

        const speeds = getWordSpeeds(this.words);
        this.playbackActive = true;

        for (let i = 0; i < this.words.length; i++) {
            if (this.destroyed) {
                console.warn(`[SpeechBubble] destroyed mid-playback at word ${i}/${this.words.length}`);
                return;
            }

            // Highlight the current word
            this.setActiveWord(i);

            const wordStr = getWord(this.words[i]);
            if (!wordStr) {
                console.warn(`[SpeechBubble] getWord(${this.words[i]}) returned null, skipping`);
                continue;
            }

            const speed: SpeedKey = speeds[i];

            try {
                await this.channel.play(wordStr, speed);
            } catch (err) {
                console.warn(`[SpeechBubble] dropped word "${wordStr}" @ ${speed}:`, err);
                await new Promise(r => setTimeout(r, 200));
            }
        }

        // Un-highlight last word (return to white) while echo rings out
        if (!this.destroyed && this.currentWordIndex >= 0 && this.currentWordIndex < this.glyphs.length) {
            this.glyphs[this.currentWordIndex].setTint(0xffffff);
            this.currentWordIndex = -1;
        }

        // Let echo tail decay before destroying
        const tailTime = this.channel.getTailTime();
        if (tailTime > 0 && !this.destroyed) {
            await new Promise(r => setTimeout(r, tailTime));
        }

        this.playbackActive = false;
        if (!this.destroyed) {
            this.destroy();
        }
    }

    private fallbackTimedDisplay(): void {
        // No audio: color each word sequentially for estimated duration, then destroy
        const speeds = getWordSpeeds(this.words);
        let delay = 0;
        for (let i = 0; i < this.words.length; i++) {
            const dur = speeds[i] === '0.5' ? 600 : speeds[i] === '1.5' ? 300 : 400;
            this.scene.time.delayedCall(delay, () => {
                if (!this.destroyed) this.setActiveWord(i);
            });
            delay += dur;
        }
        this.scene.time.delayedCall(delay, () => {
            if (!this.destroyed) this.destroy();
        });
    }

    private setActiveWord(index: number): void {
        // Un-highlight previous word (back to white)
        if (this.currentWordIndex >= 0 && this.currentWordIndex < this.glyphs.length) {
            this.glyphs[this.currentWordIndex].setTint(0xffffff);
        }

        this.currentWordIndex = index;

        // Highlight current word with its color
        if (index >= 0 && index < this.glyphs.length) {
            this.glyphs[index].setTint(getGlyphColor(this.words[index]));
        }
    }

    private computeScreenPosition(
        playerScreenX: number,
        playerScreenY: number
    ): { x: number; y: number } {
        const bubbleX = playerScreenX + TILE_SIZE / 2 - this.bubbleWidth / 2;
        const bubbleY = playerScreenY - BLOCKS_ABOVE_HEAD * TILE_SIZE - this.bubbleHeight;
        return { x: bubbleX, y: bubbleY };
    }

    updatePosition(playerScreenX: number, playerScreenY: number): void {
        const { x, y } = this.computeScreenPosition(playerScreenX, playerScreenY);
        this.container.setPosition(x, y);
    }

    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        if (this.channel && this.playbackActive) {
            this.channel.stop();
        }
        this.container.destroy();
    }

    /** Return the glyph index currently highlighted, or -1 if none. */
    getActiveGlyphIndex(): number {
        if (this.destroyed || this.currentWordIndex < 0 || this.currentWordIndex >= this.words.length) return -1;
        return this.words[this.currentWordIndex];
    }

    isActive(): boolean {
        return !this.destroyed && this.container && this.container.active;
    }

    getContainer(): Phaser.GameObjects.Container {
        return this.container;
    }
}
